import React, { useEffect, useState } from 'react';

import PersonalInfoProfileImage from './PersonalInfoProfileImage';
import {
  formatDateToDayMonthYear,
  formatDateToShortDotV2,
  useAuth,
  useIsMobile,
} from '@repo/common';

import { ReactComponent as InvisibleIcon } from '@repo/common/assets/icons/20px/icon_20px_invisible_outline.svg';
import { updateUserProfile } from '../../../../api/user/user.api';
import {
  NameEditor,
  LocationEditor,
  BirthdayEditor,
  GenderEditor,
  KoreanSkillsEditor,
  NationalityEditor,
  RoleEditor,
  CareerLevelEditor,
  VisaInfoEditor,
} from './PersonalInfoFieldEditors';

const InfoRow = ({
  label,
  value,
  visibility,
  isMobile,
  mobileRequired,
  isEditing,
  onEdit,
  onCancel,
  children,
}) => {
  const hasValue =
    value &&
    value !== 'null' &&
    (typeof value === 'string' ? value.trim() !== '' : true);
  const isPrivate = visibility === 'PRIVATE';

  if (isEditing) {
    return (
      <div className='flex flex-col w-full'>
        <div className='flex items-center justify-between w-full'>
          <div className='flex gap-0.5'>
            <div className='text-text-secondary typo-b12Strong tabletH:typo-b14'>
              {label}
            </div>
            {mobileRequired && (
              <div className='text-b14Strong text-text-accent'>*</div>
            )}
          </div>
          {isMobile && (
            <button
              type='button'
              onClick={onCancel}
              className='flex px-1.5 h-6 justify-center items-center shrink-0 underline typo-b12Strong text-text-primary'
              aria-label={`Cancel editing ${label}`}
            >
              cancel
            </button>
          )}
        </div>
        {children}
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className='flex items-start justify-between w-full gap-2'>
        <div className='flex flex-col w-full'>
          <div className='flex items-center justify-between w-full'>
            <div className='flex gap-0.5'>
              <div className='text-text-secondary typo-b12Strong'>{label}</div>
              {mobileRequired && (
                <div className='text-b14Strong text-text-accent'>*</div>
              )}
            </div>
            {onEdit && (
              <button
                type='button'
                onClick={onEdit}
                className='flex px-2 h-6 justify-center items-center shrink-0 underline typo-b12Strong text-text-primary'
                aria-label={`Edit ${label}`}
              >
                edit
              </button>
            )}
          </div>
          <div className='flex items-center gap-2 min-w-0'>
            <div
              className={`min-w-0 ${hasValue ? 'text-text-primary' : 'text-text-disable'}`}
            >
              {hasValue ? value : 'Not added yet'}
            </div>
            {isPrivate && (
              <InvisibleIcon className='text-icon-secondary w-5 h-5 shrink-0' />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='flex items-center gap-2 w-full'>
      <div className='w-[120px] shrink-0 text-text-secondary typo-b14'>
        {label}
      </div>
      <div className={hasValue ? 'text-text-primary' : 'text-text-disable'}>
        {hasValue ? value : 'Not added yet'}
      </div>
      <div className='flex w-6 h-6 shrink-0 justify-center items-center -mt-1'>
        {isPrivate && <InvisibleIcon className='text-icon-secondary' />}
      </div>
    </div>
  );
};

const DateBadge = ({ children }) => {
  if (!children) return null;

  return (
    <span className='inline-block whitespace-nowrap px-1.5 py-1 rounded-lg bg-grayscale-gray02 text-grayscale-gray05 typo-b12Strong'>
      {children}
    </span>
  );
};

const formatKoreanSkills = (koreanSkills, topikLevel, topikDate) => {
  if (topikLevel) {
    return (
      <div className='flex items-center gap-2 min-w-0'>
        <span className='min-w-0 truncate'>{topikLevel}</span>
        {topikDate && (
          <span className='shrink-0'>
            <DateBadge>Issued {formatDateToDayMonthYear(topikDate)}</DateBadge>
          </span>
        )}
      </div>
    );
  }

  return koreanSkills ?? '';
};

const formatVisaInfo = (visaInfo) => {
  if (!visaInfo) return '';

  const type = visaInfo.type ?? '';
  const expiry = visaInfo.expiryDate;

  if (!type && !expiry) return null;

  return (
    <div className='flex items-center gap-2 min-w-0'>
      <span className='min-w-0 truncate'>{type}</span>
      {expiry && (
        <span className='shrink-0'>
          <DateBadge>Expires {formatDateToDayMonthYear(expiry)}</DateBadge>
        </span>
      )}
    </div>
  );
};

const PersonalInfoFormText = () => {
  const { user, refreshUser } = useAuth();
  const isMobile = useIsMobile();
  const [editingField, setEditingField] = useState(null);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const startEdit = (field) => setEditingField(field);
  const cancelEdit = () => setEditingField(null);

  const handleSaveField = async (payload) => {
    try {
      await updateUserProfile(payload);
      await refreshUser();
      setEditingField(null);
    } catch (e) {
      console.error('필드 저장 실패', e);
    }
  };

  const renderEditor = (field) => {
    if (editingField !== field) return null;
    const Editor = {
      name: NameEditor,
      location: LocationEditor,
      birthday: BirthdayEditor,
      gender: GenderEditor,
      koreanSkills: KoreanSkillsEditor,
      nationality: NationalityEditor,
      careerRole: RoleEditor,
      careerLevel: CareerLevelEditor,
      visaInfo: VisaInfoEditor,
    }[field];
    return Editor ? <Editor onSave={handleSaveField} /> : null;
  };

  const rowProps = (field) => ({
    isMobile,
    isEditing: editingField === field,
    onEdit: () => startEdit(field),
    onCancel: cancelEdit,
    children: renderEditor(field),
  });

  return (
    <div className='flex flex-col gap-[40px] tabletH:gap-6 tabletH:pb-[60px] w-full'>
      <PersonalInfoProfileImage />

      <div className='flex flex-col items-start gap-5 tabletH:gap-6 tabletH:max-w-[568px] w-full'>
        <InfoRow
          label='Name'
          mobileRequired={true}
          value={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`}
          {...rowProps('name')}
        />

        <InfoRow
          label='Location'
          mobileRequired={true}
          value={user?.location}
          visibility={user?.visibility?.location}
          {...rowProps('location')}
        />

        <InfoRow
          label='Birthday'
          mobileRequired={true}
          value={formatDateToShortDotV2(user?.birthday)}
          visibility={user?.visibility?.birthday}
          {...rowProps('birthday')}
        />

        <InfoRow
          label='Gender'
          value={user?.gender}
          visibility={user?.visibility?.gender}
          {...rowProps('gender')}
        />

        <InfoRow
          label='Korean Skills'
          value={formatKoreanSkills(
            user?.koreanSkills,
            user?.topikLevel,
            user?.topikDate,
          )}
          visibility={user?.visibility?.koreanSkills}
          {...rowProps('koreanSkills')}
        />

        <InfoRow
          label='Nationality'
          value={user?.nationality}
          visibility={user?.visibility?.nationality}
          {...rowProps('nationality')}
        />

        <InfoRow
          label='Role'
          value={user?.careerRole}
          visibility={user?.visibility?.careerRole}
          {...rowProps('careerRole')}
        />

        <InfoRow
          label='Career level'
          value={user?.careerLevel}
          visibility={user?.visibility?.careerLevel}
          {...rowProps('careerLevel')}
        />

        <InfoRow
          label='Visa info'
          value={formatVisaInfo(user?.visaInfo)}
          visibility={user?.visibility?.visaInfo}
          {...rowProps('visaInfo')}
        />
      </div>
    </div>
  );
};

export default PersonalInfoFormText;
